// src/features/metricas/MetricasManagement.jsx - VERSIÓN ESTABLE
import React, { useState, useEffect, useCallback } from 'react';
import { metricasService } from '../../api/metricas';
import MetricasEmpresas from './components/MetricasEmpresas';
import MetricasContadores from './components/MetricasContadores';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { 
    ChartBarIcon,
    ArrowPathIcon,
    BuildingOfficeIcon,
    UserGroupIcon,
    DocumentArrowDownIcon
} from '@heroicons/react/24/outline';

const MetricasManagement = () => {
    // Estados principales
    const [datosOriginales, setDatosOriginales] = useState({
        empresas: { metricas: [], resumen: {} },
        contadores: { metricas: [], resumen: {} }
    });
    
    const [datosFiltrados, setDatosFiltrados] = useState({
        empresas: { metricas: [], resumen: {} },
        contadores: { metricas: [], resumen: {} }
    });
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('empresas');
    const [lastRefresh, setLastRefresh] = useState(new Date());
    
    // Estado único para filtros
    const [filters, setFilters] = useState({
        // Filtros generales
        fecha_inicio: '',
        fecha_fin: '',
        empresa_nombre: '',
        contador_username: '',
        // Filtros específicos (select)
        empresa_seleccionada: '',
        contador_seleccionado: ''
    });

    // Cargar datos iniciales
    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const [empresasResponse, contadoresResponse] = await Promise.all([
                metricasService.getMetricasEmpresasDetalladas({}),
                metricasService.getMetricasContadoresDetalladas({})
            ]);
            
            // Validar y asegurar estructura correcta
            const datosEmpresas = {
                metricas: Array.isArray(empresasResponse?.metricas) ? empresasResponse.metricas : [],
                resumen: empresasResponse?.resumen || {}
            };
            
            const datosContadores = {
                metricas: Array.isArray(contadoresResponse?.metricas) ? contadoresResponse.metricas : [],
                resumen: contadoresResponse?.resumen || {}
            };
            
            setDatosOriginales({
                empresas: datosEmpresas,
                contadores: datosContadores
            });
            
            // Inicialmente, los datos filtrados son iguales a los originales
            setDatosFiltrados({
                empresas: datosEmpresas,
                contadores: datosContadores
            });
            
            setLastRefresh(new Date());
        } catch (error) {
            //console.error('❌ Error cargando métricas:', error);
            setError(error.message || 'Error al cargar las métricas');
        } finally {
            setLoading(false);
        }
    };

    // 🔥 FUNCIÓN DE FILTRADO - VERSIÓN SIMPLE Y SEGURA
    const aplicarFiltros = useCallback(() => {
        //console.log('🎯 Aplicando filtros:', filters);
        
        // 🔥 FUNCIÓN PARA FILTRAR EMPRESAS
        const filtrarEmpresas = () => {
            if (!datosOriginales.empresas.metricas || !Array.isArray(datosOriginales.empresas.metricas)) {
                return { metricas: [], resumen: {} };
            }

            let resultado = [...datosOriginales.empresas.metricas];
            //console.log('🏢 Empresas originales:', resultado.length);

            // 1. Filtrar por fecha (general)
            if (filters.fecha_inicio) {
                const inicio = new Date(filters.fecha_inicio);
                inicio.setHours(0, 0, 0, 0);
                resultado = resultado.filter(item => {
                    if (!item.fecha_acta) return false;
                    const fechaActa = new Date(item.fecha_acta);
                    fechaActa.setHours(0, 0, 0, 0);
                    return fechaActa >= inicio;
                });
            }

            if (filters.fecha_fin) {
                const fin = new Date(filters.fecha_fin);
                fin.setHours(23, 59, 59, 999);
                resultado = resultado.filter(item => {
                    if (!item.fecha_acta) return false;
                    const fechaActa = new Date(item.fecha_acta);
                    return fechaActa <= fin;
                });
            }

            // 2. Filtrar por NOMBRE de empresa (búsqueda de texto)
            if (filters.empresa_nombre) {
                const busqueda = filters.empresa_nombre.toLowerCase().trim();
                resultado = resultado.filter(item => 
                    item.empresa_nombre && 
                    item.empresa_nombre.toLowerCase().includes(busqueda)
                );
            }

            // 3. Filtrar por empresa seleccionada (dropdown - cuando se usa el select)
            if (filters.empresa_seleccionada && filters.empresa_seleccionada !== 'todas') {
                resultado = resultado.filter(item => 
                    item.empresa_nombre === filters.empresa_seleccionada
                );
            }

            // 4. 🔥 NO filtrar por contador en empresas (excepto si es búsqueda cruzada)
            //    Pero sí podemos filtrar por contador si el usuario lo busca específicamente
            if (filters.contador_username) {
                const busqueda = filters.contador_username.toLowerCase().trim();
                resultado = resultado.filter(item => 
                    item.profesional_username && 
                    item.profesional_username.toLowerCase().includes(busqueda)
                );
            }

            //console.log('🏢 Empresas después de filtrar:', resultado.length);
            return resultado;
        };

        // 🔥 FUNCIÓN PARA FILTRAR CONTADORES
        const filtrarContadores = () => {
            if (!datosOriginales.contadores.metricas || !Array.isArray(datosOriginales.contadores.metricas)) {
                return { metricas: [], resumen: {} };
            }

            let resultado = [...datosOriginales.contadores.metricas];
            //console.log('👤 Contadores originales:', resultado.length);

            // 1. Filtrar por fecha (general)
            if (filters.fecha_inicio) {
                const inicio = new Date(filters.fecha_inicio);
                inicio.setHours(0, 0, 0, 0);
                resultado = resultado.filter(item => {
                    if (!item.fecha_acta) return false;
                    const fechaActa = new Date(item.fecha_acta);
                    fechaActa.setHours(0, 0, 0, 0);
                    return fechaActa >= inicio;
                });
            }

            if (filters.fecha_fin) {
                const fin = new Date(filters.fecha_fin);
                fin.setHours(23, 59, 59, 999);
                resultado = resultado.filter(item => {
                    if (!item.fecha_acta) return false;
                    const fechaActa = new Date(item.fecha_acta);
                    return fechaActa <= fin;
                });
            }

            // 2. Filtrar por NOMBRE de contador (búsqueda de texto)
            if (filters.contador_username) {
                const busqueda = filters.contador_username.toLowerCase().trim();
                resultado = resultado.filter(item => 
                    item.profesional_username && 
                    item.profesional_username.toLowerCase().includes(busqueda)
                );
            }

            // 3. Filtrar por contador seleccionado (dropdown - cuando se usa el select)
            if (filters.contador_seleccionado && filters.contador_seleccionado !== 'todos') {
                resultado = resultado.filter(item => 
                    item.profesional_username === filters.contador_seleccionado
                );
            }

            // 4. 🔥 NO filtrar por empresa en contadores (excepto si es búsqueda cruzada)
            //    Pero sí podemos filtrar por empresa si el usuario lo busca específicamente
            if (filters.empresa_nombre) {
                const busqueda = filters.empresa_nombre.toLowerCase().trim();
                resultado = resultado.filter(item => 
                    item.empresa_nombre && 
                    item.empresa_nombre.toLowerCase().includes(busqueda)
                );
            }

            //console.log('👤 Contadores después de filtrar:', resultado.length);
            return resultado;
        };

        // 🔥 APLICAR FILTRADO
        const empresasFiltradas = filtrarEmpresas();
        const contadoresFiltrados = filtrarContadores();

        // 🔥 CALCULAR RESUMENES
        const calcularResumen = (datos) => {
            if (!datos || !Array.isArray(datos) || datos.length === 0) {
                return {
                    total_empresas: 0,
                    total_contadores: 0,
                    total_actas: 0,
                    total_horas: 0,
                    total_valor: 0,
                    cantidad_empresas: 0,
                    cantidad_contadores: 0,
                };
            }

            const empresasUnicas = new Set();
            const contadoresUnicos = new Set();
            
            let totalHoras = 0;
            let totalValor = 0;

            datos.forEach(item => {
                if (item.empresa_id || item.empresa_nombre) {
                    empresasUnicas.add(item.empresa_id || item.empresa_nombre);
                }
                if (item.profesional_id || item.profesional_username) {
                    contadoresUnicos.add(item.profesional_id || item.profesional_username);
                }
                
                totalHoras += parseFloat(item.duracion_horas) || 0;
                totalValor += parseFloat(item.valor_total_acta) || 0;
            });

            return {
                total_empresas: empresasUnicas.size,
                total_contadores: contadoresUnicos.size,
                total_actas: datos.length,
                total_horas: totalHoras,
                total_valor: totalValor,
                cantidad_empresas: empresasUnicas.size,
                cantidad_contadores: contadoresUnicos.size,
            };
        };

        const nuevosDatosFiltrados = {
            empresas: {
                metricas: empresasFiltradas,
                resumen: calcularResumen(empresasFiltradas)
            },
            contadores: {
                metricas: contadoresFiltrados,
                resumen: calcularResumen(contadoresFiltrados)
            }
        };

        //console.log('📊 RESULTADO FILTRADO:', {
            //empresas: {
            //    metricas: nuevosDatosFiltrados.empresas.metricas.length,
            //    resumen: nuevosDatosFiltrados.empresas.resumen
            //},
            //contadores: {
            //    metricas: nuevosDatosFiltrados.contadores.metricas.length,
            //    resumen: nuevosDatosFiltrados.contadores.resumen
            //},
            // 🔥 VERIFICAR SI SON DIFERENTES
            sonDiferentes: nuevosDatosFiltrados.empresas.metricas.length !== nuevosDatosFiltrados.contadores.metricas.length
        //});

        setDatosFiltrados(nuevosDatosFiltrados);
        
    }, [datosOriginales, filters]);

    // 🔥 EJECUTAR FILTRADO CUANDO CAMBIAN LOS FILTROS O DATOS ORIGINALES
    useEffect(() => {
        if (datosOriginales.empresas.metricas.length > 0 || datosOriginales.contadores.metricas.length > 0) {
            aplicarFiltros();
        }
    }, [filters, datosOriginales, aplicarFiltros]);

    // 🔥 MANEJADOR DE CAMBIO DE FILTROS
    const handleFiltersChange = (nuevosFiltros) => {
        //console.log('🔄 Filtros cambiando desde hijo:', nuevosFiltros);
        
        setFilters(prev => {
            const nuevos = { ...prev };
            
            // Actualizar filtros comunes
            if ('fecha_inicio' in nuevosFiltros) {
                nuevos.fecha_inicio = nuevosFiltros.fecha_inicio;
            }
            if ('fecha_fin' in nuevosFiltros) {
                nuevos.fecha_fin = nuevosFiltros.fecha_fin;
            }
            
            // 🔥 SI ESTAMOS EN EMPRESAS: actualizar filtros de empresa, limpiar filtros de contador
            if (activeTab === 'empresas') {
                if ('empresa_nombre' in nuevosFiltros) {
                    nuevos.empresa_nombre = nuevosFiltros.empresa_nombre;
                }
                if ('empresa_filtro' in nuevosFiltros) {
                    nuevos.empresa_seleccionada = nuevosFiltros.empresa_filtro;
                }
                // Limpiar filtros de contador cuando estamos en empresas
                nuevos.contador_username = '';
                nuevos.contador_seleccionado = '';
            }
            
            // 🔥 SI ESTAMOS EN CONTADORES: actualizar filtros de contador, limpiar filtros de empresa
            if (activeTab === 'contadores') {
                if ('contador_username' in nuevosFiltros) {
                    nuevos.contador_username = nuevosFiltros.contador_username;
                }
                if ('contador_filtro' in nuevosFiltros) {
                    nuevos.contador_seleccionado = nuevosFiltros.contador_filtro;
                }
                // Limpiar filtros de empresa cuando estamos en contadores
                nuevos.empresa_nombre = '';
                nuevos.empresa_seleccionada = '';
            }
            
            //console.log('✅ Filtros actualizados:', nuevos);
            return nuevos;
        });
    };

    // 🔥 LIMPIAR FILTROS
    const handleClearFilters = () => {
        setFilters({
            fecha_inicio: '',
            fecha_fin: '',
            empresa_nombre: '',
            contador_username: '',
            empresa_seleccionada: '',
            contador_seleccionado: ''
        });
    };

    // 🔥 ACTUALIZAR DATOS
    const handleRefresh = () => {
        cargarDatos();
    };

    // 🔥 EXPORTAR
    const handleExportCompleto = async () => {
        try {
            await metricasService.exportActasCompletasExcel(filters);
        } catch (error) {
            //console.error('❌ Error exportando:', error);
            setError('Error al exportar: ' + error.message);
        }
    };

    // Mostrar loading inicial
    if (loading && datosOriginales.empresas.metricas.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <LoadingSpinner text="Cargando métricas..." />
            </div>
        );
    }

    // Mostrar error
    if (error && !loading) {
        return (
            <div className="max-w-7xl mx-auto px-6 py-12">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-center">
                        <svg className="w-8 h-8 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <h3 className="text-lg font-semibold text-red-800">Error</h3>
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleRefresh}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    //console.log('🔄 Datos para dashboard:', {
   //activeTab,
        //empresasMetricasLength: datosFiltrados.empresas.metricas?.length || 0,
        //empresasResumenKeys: Object.keys(datosFiltrados.empresas.resumen || {}),
        //contadoresMetricasLength: datosFiltrados.contadores.metricas?.length || 0,
        //contadoresResumenKeys: Object.keys(datosFiltrados.contadores.resumen || {})
    //});

    return (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center">
                        <ChartBarIcon className="h-8 w-8 text-purple-600 mr-3" />
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Métricas y Reportes</h1>
                            <p className="text-sm sm:text-base text-gray-600">
                                {activeTab === 'empresas' 
                                    ? `Por empresas: ${datosFiltrados.empresas.metricas.length} registros`
                                    : `Por contadores: ${datosFiltrados.contadores.metricas.length} registros`
                                }
                            </p>
                        </div>
                    </div>
                
                    <div className="flex items-center space-x-3">
                        <div className="text-xs text-gray-500 text-right">
                            <div>Última actualización:</div>
                            <div className="font-medium">
                                {lastRefresh.toLocaleTimeString('es-ES', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                })}
                            </div>
                        </div>
                        
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Actualizar datos"
                        >
                            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        
                        <button
                            onClick={handleExportCompleto}
                            disabled={loading}
                            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md shadow-sm hover:bg-purple-700 text-sm font-medium disabled:opacity-50"
                        >
                            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                            Exportar Todo
                        </button>
                    </div>
                </div>
            </div>

            {/* Pestañas */}
            <div className="mb-6">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('empresas')}
                            disabled={loading}
                            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center disabled:opacity-50 ${
                                activeTab === 'empresas'
                                ? 'border-purple-500 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                            Por Empresas
                            <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
                                {datosFiltrados.empresas.metricas.length}
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('contadores')}
                            disabled={loading}
                            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center disabled:opacity-50 ${
                                activeTab === 'contadores'
                                ? 'border-purple-500 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <UserGroupIcon className="h-4 w-4 mr-2" />
                            Por Contadores
                            <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                                {datosFiltrados.contadores.metricas.length}
                            </span>
                        </button>
                    </nav>
                </div>
            </div>

            {/* Tabla de datos filtrados */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                {loading && datosFiltrados.empresas.metricas.length === 0 && datosFiltrados.contadores.metricas.length === 0 ? (
                    <div className="flex justify-center items-center h-64">
                        <LoadingSpinner text="Cargando datos..." />
                    </div>
                ) : activeTab === 'empresas' ? (
                        <MetricasEmpresas 
                            metricas={datosFiltrados.empresas.metricas}
                            resumen={datosFiltrados.empresas.resumen}
                            filters={{
                                fecha_inicio: filters.fecha_inicio,
                                fecha_fin: filters.fecha_fin,
                                empresa_nombre: filters.empresa_nombre,
                                empresa_filtro: filters.empresa_seleccionada,
                                // 🔥 NO pasar contador_username a empresas (a menos que sea búsqueda cruzada)
                                contador_username: ''  // Limpiar este filtro para empresas
                            }}
                            onFiltersChange={handleFiltersChange}
                            loading={loading}
                            onExport={async (exportFilters) => {
                                try {
                                    await metricasService.exportMetricasEmpresasExcel(exportFilters);
                                } catch (error) {
                                    //console.error('Error exportando empresas:', error);
                                    setError('Error al exportar empresas: ' + error.message);
                                }
                            }}
                        />
                    ) : (
                        <MetricasContadores 
                            metricas={datosFiltrados.contadores.metricas}
                            resumen={datosFiltrados.contadores.resumen}
                            filters={{
                                fecha_inicio: filters.fecha_inicio,
                                fecha_fin: filters.fecha_fin,
                                contador_username: filters.contador_username,
                                contador_filtro: filters.contador_seleccionado,
                                // 🔥 NO pasar empresa_nombre a contadores (a menos que sea búsqueda cruzada)
                                empresa_nombre: ''  // Limpiar este filtro para contadores
                            }}
                            onFiltersChange={handleFiltersChange}
                            loading={loading}
                            onExport={async (exportData) => {
                                try {
                                    if (exportData.esTodos) {
                                        await metricasService.exportMetricasContadoresExcel(exportData.filters);
                                    } else {
                                        await metricasService.exportMetricasProfesionalExcel(
                                            exportData.username, 
                                            exportData.filters
                                        );
                                    }
                                } catch (error) {
                                    //console.error('Error exportando contadores:', error);
                                    setError('Error al exportar contadores: ' + error.message);
                                }
                            }}
                        />
                    )
                }
            </div>

            {/* Estado actual */}
            <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Estado actual:</h4>
                        <ul className="space-y-1 text-gray-600">
                            <li>• Pestaña activa: <span className="font-medium">{activeTab === 'empresas' ? 'Empresas' : 'Contadores'}</span></li>
                            <li>• Registros empresas: <span className="font-medium">{datosFiltrados.empresas.metricas.length}</span></li>
                            <li>• Registros contadores: <span className="font-medium">{datosFiltrados.contadores.metricas.length}</span></li>
                            <li>• Filtros aplicados: {
                                Object.values(filters).filter(v => v).length > 0 
                                    ? <span className="font-medium text-green-600">Sí</span> 
                                    : <span className="font-medium text-gray-500">No</span>
                            }</li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-medium text-gray-900 mb-2">Resumen dashboard:</h4>
                        <ul className="space-y-1 text-gray-600">
                            <li>• Actas empresas: <span className="font-medium">{datosFiltrados.empresas.resumen.total_actas || 0}</span></li>
                            <li>• Actas contadores: <span className="font-medium">{datosFiltrados.contadores.resumen.total_actas || 0}</span></li>
                            <li>• Total horas: <span className="font-medium">{datosFiltrados.empresas.resumen.total_horas?.toFixed(2) || 0} hrs</span></li>
                            <li>• Total valor: <span className="font-medium">
                                ${(datosFiltrados.empresas.resumen.total_valor || 0).toLocaleString('es-CO')}
                            </span></li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MetricasManagement;
